import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { type HashCheckResult, verifyApi } from '@/api/verify.api';
import { formatDateTime } from '@/lib/format';
import BrandMark from '@/components/BrandMark';

export function VerifyPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['verify', id],
    queryFn: () => verifyApi.get(id!),
    enabled: !!id,
    retry: false,
  });

  const [hashInput, setHashInput] = useState('');
  const [hashResult, setHashResult] = useState<HashCheckResult | null>(null);

  const checkMutation = useMutation({
    mutationFn: () => verifyApi.checkHash(id!, hashInput),
    onSuccess: (r) => setHashResult(r),
  });

  if (!id) return <Wrap><Center>—</Center></Wrap>;
  if (isLoading) return <Wrap><Center>{t('common.loading')}</Center></Wrap>;
  if (isError || !data) {
    return (
      <Wrap>
        <Center>
          <div className="text-2xl mb-2">⚠️</div>
          <div className="font-semibold text-gray-900">{t('verify.not_found_title')}</div>
          <div className="text-[13px] text-muted mt-1">{t('verify.not_found_text')}</div>
        </Center>
      </Wrap>
    );
  }

  const statusColor =
    data.status === 'completed' ? 'bg-success text-white' :
    data.status === 'declined' ? 'bg-danger text-white' :
    data.status === 'voided' || data.status === 'expired' ? 'bg-gray-400 text-white' :
    'bg-warning text-white';

  return (
    <Wrap>
      <div className="text-center mb-6">
        <div className={`inline-block px-3 py-1.5 rounded-full text-[12px] uppercase font-semibold ${statusColor}`}>
          {data.status.replace('_', ' ')}
        </div>
        <h1 className="text-2xl font-semibold text-gray-900 mt-3">{data.title}</h1>
        <p className="text-[13px] text-muted mt-1">
          From <strong>{data.organizationName}</strong> · ID <code className="text-[11px]">{data.id}</code>
        </p>
      </div>

      <div className="card mb-4">
        <h3 className="text-base font-semibold text-gray-900 mb-3">{t('verify.timeline')}</h3>
        <dl className="text-[13px] space-y-1.5">
          <Row label={t('verify.sent')} value={data.sentAt ? formatDateTime(data.sentAt) : '—'} />
          <Row label={t('verify.completed')} value={data.completedAt ? formatDateTime(data.completedAt) : '—'} />
          <Row label={t('verify.workflow')} value={data.workflow} />
        </dl>
      </div>

      <div className="card mb-4">
        <h3 className="text-base font-semibold text-gray-900 mb-3">{t('verify.recipients')}</h3>
        <ul className="space-y-2">
          {data.recipients.map((r) => {
            const ok = r.status === 'signed';
            const bad = r.status === 'declined';
            return (
              <li key={r.id} className="flex items-center gap-3 border-b border-border last:border-b-0 pb-2 last:pb-0">
                <span
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-semibold shrink-0 ${
                    ok ? 'bg-success/15 text-success' : bad ? 'bg-danger/15 text-danger' : 'bg-gray-100 text-muted'
                  }`}
                >
                  {ok ? '✓' : bad ? '✗' : r.signingOrder}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-gray-900">{r.name}</div>
                  <div className="text-[11px] text-muted">{r.email} · {r.role}</div>
                </div>
                <div className="text-[11px] text-muted text-right shrink-0">
                  <div>{r.status}</div>
                  {r.signedAt && <div>{formatDateTime(r.signedAt)}</div>}
                  {r.declinedAt && <div>{formatDateTime(r.declinedAt)}</div>}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="card mb-4">
        <h3 className="text-base font-semibold text-gray-900 mb-3">{t('verify.documents')}</h3>
        <ul className="space-y-3">
          {data.documents.map((d) => (
            <li key={d.id} className="border-b border-border last:border-b-0 pb-3 last:pb-0">
              <div className="font-medium text-gray-900 text-[14px]">{d.name}</div>
              <div className="text-[12px] text-muted">{d.pages} {t('verify.pages')}</div>
              <div className="mt-2 space-y-1 text-[11px] font-mono">
                <div>
                  <span className="text-muted">{t('verify.original_hash')}</span>{' '}
                  <span className="text-gray-700 break-all">{d.originalSha256}</span>
                </div>
                {d.signedSha256 && (
                  <div>
                    <span className="text-muted">{t('verify.signed_hash')}</span>{' '}
                    <span className="text-gray-700 break-all">{d.signedSha256}</span>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="card mb-4">
        <h3 className="text-base font-semibold text-gray-900 mb-1">{t('verify.integrity_title')}</h3>
        <p className="text-[12px] text-muted mb-3">
          {t('verify.integrity_text', {
            cmd1: 'shasum -a 256 file.pdf',
            cmd2: 'Get-FileHash file.pdf',
          })}
        </p>
        <div className="flex gap-2">
          <input
            className="input flex-1 font-mono text-[12px]"
            placeholder={t('verify.hash_placeholder')}
            value={hashInput}
            onChange={(e) => {
              setHashInput(e.target.value.trim());
              setHashResult(null);
            }}
          />
          <button
            onClick={() => checkMutation.mutate()}
            disabled={hashInput.length !== 64 || checkMutation.isPending}
            className="btn btn-primary"
          >
            {checkMutation.isPending ? t('verify.checking') : t('verify.verify_hash')}
          </button>
        </div>
        {hashResult && (
          <div
            className={`mt-3 p-3 rounded-md text-[13px] ${
              hashResult.match
                ? 'bg-success/10 text-success-fg border border-success/30'
                : 'bg-danger/10 text-danger-fg border border-danger/30'
            }`}
          >
            {hashResult.match ? (
              <>
                <strong>{t('verify.match')}</strong>{' '}
                {t('verify.match_text', { kind: hashResult.document.kind, name: hashResult.document.name })}
              </>
            ) : (
              <>
                <strong>{t('verify.no_match')}</strong>{' '}
                {hashResult.error ?? t('verify.no_match_text')}
              </>
            )}
          </div>
        )}
      </div>

      <p className="text-center text-[11px] text-muted mt-6">{t('verify.footer')}</p>
    </Wrap>
  );
}

function Wrap({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-bg py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <BrandMark size={32} radius={7} />
          <strong className="text-[16px] text-gray-900">Documentology Verification</strong>
        </div>
        {children}
      </div>
    </div>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return <div className="card text-center py-16 text-muted">{children}</div>;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted">{label}</dt>
      <dd className="text-gray-900 text-right">{value}</dd>
    </div>
  );
}
